import { Controller, type UseFormReturn, useFieldArray } from "react-hook-form";

import { ChevronRight, Plus, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TINA_STAGES } from "@/lib/tina/constants";
import type { AdvancedConfigType } from "@/pages/tina/runs/schemas/run.create.form";

interface RunAdvancedConfigProps {
  form: UseFormReturn<AdvancedConfigType>;
}

function emptyToUndefined(value: string) {
  return value === "" ? undefined : Number(value);
}

export function RunAdvancedConfig({ form }: RunAdvancedConfigProps) {
  const { register, control } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "prompt_overrides",
  });

  return (
    <Collapsible className="rounded-lg border bg-card">
      <CollapsibleTrigger className="group flex w-full items-center gap-2 p-3 text-sm font-semibold">
        <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
        Advanced configuration
        <Badge variant="outline" className="ml-1 font-normal">
          optional
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="grid gap-4 border-t p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium" htmlFor="run-limit">
              Limit{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </label>
            <Input
              id="run-limit"
              type="number"
              min={1}
              placeholder="e.g. 50"
              {...register("limit", { setValueAs: emptyToUndefined })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium" htmlFor="run-score">
              Score threshold
            </label>
            <Input
              id="run-score"
              type="number"
              step="0.01"
              min={0}
              max={1}
              placeholder="0.0 – 1.0"
              {...register("score_threshold", { setValueAs: emptyToUndefined })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium" htmlFor="run-cost">
              Cost cap (USD)
            </label>
            <Input
              id="run-cost"
              type="number"
              step="0.01"
              min={0}
              placeholder="e.g. 5.00"
              {...register("cost_cap_usd", { setValueAs: emptyToUndefined })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium">Dry run</span>
            <Controller
              control={control}
              name="dry_run"
              render={({ field }) => (
                <div className="flex h-10 items-center gap-2">
                  <Switch
                    checked={Boolean(field.value)}
                    onCheckedChange={field.onChange}
                  />
                  <span className="text-sm text-muted-foreground">
                    {field.value ? "On" : "Off"}
                  </span>
                </div>
              )}
            />
          </div>

          <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-4">
            <span className="text-xs font-medium">Prompt overrides</span>
            {fields.map((row, index) => (
              <div key={row.id} className="flex items-center gap-2">
                <Controller
                  control={control}
                  name={`prompt_overrides.${index}.stage`}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-44">
                        <SelectValue placeholder="Stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {TINA_STAGES.map((stage) => (
                          <SelectItem key={stage} value={stage}>
                            {stage}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <Input
                  className="flex-1"
                  placeholder="prompt_version_id (uuid)"
                  {...register(`prompt_overrides.${index}.prompt_version_id`)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove override"
                  onClick={() => remove(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() =>
                append({ stage: TINA_STAGES[0], prompt_version_id: "" })
              }
            >
              <Plus className="mr-1 h-4 w-4" />
              Add override
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
